/** @prettier */

/**
 * アクティベーション中のゲートウェイ一覧
 */

import {useState, useEffect, useRef} from 'react';
import {
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Button,
  Box,
  Stack,
} from '@mui/material';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import GWAddDialog from './GWAddDialog';
import APIErrorMessage from '../../component/APIErrorMessage';
import CardContents from '../../component/CardContents';
//API import
import {
  deleteCompositionsActivation,
  getCompositionsActivation,
  postCompositionsActivation,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {sortByUnicode} from '../../functions/sort';
import {iconBlack, iconWhite} from '../../define';

const GWActivate = (props: {
  activateButtonName: string;
  tempActivateGW: apiIF.res.getCompositionsActivation | null;
  setTempActivateGW: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsActivation | null>
  >;
  gwInfo: apiIF.res.getCompositionsGateway | null;
  setExchangeGW: React.Dispatch<React.SetStateAction<string[]>>;
  setRestActivationTime: React.Dispatch<React.SetStateAction<string[]>>;
  restActivationTime: string[];
}) => {
  //パスパラメータ取得
  const {siteID} = useParams();

  const [now, setNow] = useState(new Date()); //現在時刻
  const [countDownFlag, setCountDownFlag] = useState(false); //減算処理実施フラグ
  const [deleteDisabled, setDeleteDisabled] = useState<boolean[]>([]); //Deleteボタン非表示ボタン
  const [retryDisabled, setRetryDisabled] = useState<boolean[]>([]); //ReTryボタン非表示ボタン
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //API使用時エラー発生ダイアログ表示フラグ

  const trans = useTranslation().t; //翻訳関数

  //仮登録されたゲートウェイの情報を取得
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) return;

    getCompositionsActivation(siteID, {'max-result': 100}).then(response => {
      //表示内容をUnicode順に並び替え
      response['gateway-informations'].sort(sortByUnicode('gateway-name'));
      props.setTempActivateGW(response);
      //残り時間カウント用に配列をコピー
      props.setRestActivationTime(
        response['gateway-informations'].map(info => info['time-remaining'])
      );
      countDown();
      // 引継ぎ実施中のゲートウェイリストを作成
      let exchangeGWList: Array<string> = [];
      response['gateway-informations'].forEach(info => {
        if (info['exchange-gateway-id'] !== '') {
          exchangeGWList.push(info['exchange-gateway-id']);
        }
      });
      props.setExchangeGW(exchangeGWList);
    });
  }, [siteID]);

  //set intervalは起動時点のstate値を記憶する。以降のカウントダウンでは常に
  //初期値からデクリメントするため、正しくカウントダウンできない。
  //そこで、カウントダウン毎に値を取得する仕組みとしている
  const refRestActivationTime = useRef(props.restActivationTime);
  useEffect(() => {
    refRestActivationTime.current = props.restActivationTime;
  }, [props.restActivationTime]);

  //10秒毎にゲートウェイ情報を取得する
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) return;

    const intervalId = setInterval(() => {
      setNow(new Date()); //useEffectが実行される度に、stateを更新し、次のuseEffectを実行する
      //仮登録されたゲートウェイの情報を取得
      getCompositionsActivation(siteID, {'max-result': 100}).then(response => {
        //表示内容をUnicode順に並び替え
        response['gateway-informations'].sort(sortByUnicode('gateway-name'));
        props.setTempActivateGW(response);
        let exchangeGWList: Array<string> = [];
        response['gateway-informations'].forEach((info, index) => {
          if (info['exchange-gateway-id'] !== '') {
            // 引継ぎ実施中のゲートウェイ一覧を
            exchangeGWList.push(info['exchange-gateway-id']);

            // 引継ぎゲートウェイの有効期限切れの場合は自動で削除する
            // 新規追加ゲートウェイは期限切れでも消さない
            if (info['time-remaining'] === '0') {
              deleteTempGW(info['gateway-name'], index);
            }
          }
        });
        // 引継ぎ実施中のゲートウェイ一覧を更新する
        props.setExchangeGW(exchangeGWList);
      });
    }, 10000);

    //10秒毎にタイマを削除
    return function () {
      clearInterval(intervalId);
    };
  }, [now]);

  /**
   * 1秒毎に残り時間の減算を実施
   */
  const countDown = () => {
    if (countDownFlag) return;

    setInterval(() => {
      //すべてのアクティベート中GWに対して、残り時間を減算する
      props.setRestActivationTime(
        refRestActivationTime.current.map(time => {
          let num = Number(time); //string型をnumber型に変換
          if (num > 0) num--;
          return String(num); //number型をstring型に変換
        })
      );
    }, 1000);
    //setIntervalが複数回実行されないようフラグで管理
    //ページリロード時は初期値でfalseとなる
    setCountDownFlag(true);
  };

  /**
   * Deleteボタン押下時処理
   * @param gwName ゲートウェイの名前
   * @param index ゲートウェイの通し番号
   */
  const deleteTempGW = (gwName: string, index: number) => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) return;

    //配列を持つstateを更新する場合破壊的な変更ができないため、別変数に参照渡し以外の方法でコピーして変更する必要がある
    let disable = [...deleteDisabled];
    disable[index] = true; //押下した削除ボタンのdisableをtrue
    setDeleteDisabled(disable); //stateに変更後の配列を戻す

    //アクティベート中GWの削除
    deleteCompositionsActivation(siteID, {'gateway-name': gwName})
      .then(() => {
        //仮登録されたゲートウェイの情報を取得
        return getCompositionsActivation(siteID, {'max-result': 100});
      })
      .then(response => {
        //表示内容をUnicode順に並び替え
        response['gateway-informations'].sort(sortByUnicode('gateway-name'));
        props.setTempActivateGW(response);
        //残り時間カウント用の配列を作成
        props.setRestActivationTime(
          response['gateway-informations'].map(info => info['time-remaining'])
        );
        // 引継ぎ実施中のゲートウェイ一覧を更新する
        let exchangeGWList: Array<string> = [];
        response['gateway-informations'].forEach(info => {
          if (info['exchange-gateway-id'] !== '') {
            exchangeGWList.push(info['exchange-gateway-id']);
          }
        });
        props.setExchangeGW(exchangeGWList);
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        disable[index] = false; //disableの解除
        setDeleteDisabled(disable); //stateに変更後の配列を戻す
      });
  };
  /**
   * ReTryボタン押下時処理（削除して登録）
   * @param gwName ゲートウェイの名前
   * @param activationCode 代理店コード
   * @param index ゲートウェイの通し番号
   */
  const retryGWAdd = (
    gwName: string,
    activationCode: string,
    index: number
  ) => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) return;

    //配列を持つstateを更新する場合破壊的な変更ができないため、別変数に参照渡し以外の方法でコピーして変更する必要がある
    let disable = [...retryDisabled];
    disable[index] = true; //押下した削除ボタンのdisableをtrue
    setRetryDisabled(disable); //stateに変更後の配列を戻す

    //アクティベート中GWの削除
    deleteCompositionsActivation(siteID, {'gateway-name': gwName})
      .then(() => {
        //ゲートウェイの仮登録処理
        return postCompositionsActivation(siteID, {
          'gateway-name': gwName,
          'agency-code': activationCode,
        });
      })
      .then(() => {
        //仮登録されたゲートウェイの情報を取得
        return getCompositionsActivation(siteID, {'max-result': 100});
      })
      .then(response => {
        //表示内容をUnicode順に並び替え
        response['gateway-informations'].sort(sortByUnicode('gateway-name'));
        props.setTempActivateGW(response);
        //残り時間カウント用の配列を作成
        props.setRestActivationTime(
          response['gateway-informations'].map(info => info['time-remaining'])
        );
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        disable[index] = false; //disableの解除
        setRetryDisabled(disable); //stateに変更後の配列を戻す
      });
  };

  /**
   * 残り時間を表示する文字を作成
   * @param index ゲートウェイの通し番号
   * @returns JSX
   */
  const TimeRemainCalc = (index: number) => {
    //秒⇒分と秒に加工
    let numSecond = Number(props.restActivationTime[index]);
    const minutes = Math.floor(numSecond / 60);
    const second = Math.floor(numSecond % 60);

    //prettier-ignore
    //2桁表示に加工
    return (
      <>
        {`${String(minutes).padStart(2, '0')}:${String(second).padStart(2, '0')}`}
      </>
    );
  };

  return (
    <>
      {/* ゲートウェイ追加ボタン */}
      <Box className="gwSetting__contents--gw gwSetting__contents__button--add">
        <GWAddDialog
          activateButtonName={props.activateButtonName}
          tempActivateGW={props.tempActivateGW}
          setTempActivateGW={props.setTempActivateGW}
          setRestActivationTime={props.setRestActivationTime}
          gwInfo={props.gwInfo}
        />
      </Box>
      {/* アクティベート中GW */}
      {props.tempActivateGW !== null &&
      props.restActivationTime.length !== 0 ? (
        props.tempActivateGW['gateway-informations'].map((tempGW, index) => (
          <Card
            id={`mv-BoxGw${tempGW['gateway-name']}`}
            key={`${index}-${tempGW['gateway-name']}`}
            className="gwSetting__contents--gw">
            <CardContent>
              <Stack spacing={4}>
                {/* 状態値とゲートウェイの名前とアクティベーション用ボタン */}
                <Grid
                  container
                  className="gwSetting__contents__title"
                  spacing={1}
                  justifyContent="flex-end">
                  {/* 現在のゲートウェイの状態値マーク */}
                  <Grid item xs={1} lg={1}>
                    {tempGW['time-remaining'] !== '0' ? (
                      <CircularProgress size={35} />
                    ) : (
                      <img
                        className="gwSetting__contents__successMark"
                        src={iconBlack.FAILURE.src}
                        alt={`${trans(
                          `GWActivate.image.alternativeText.${iconBlack.FAILURE.alt}`
                        )}`}
                      />
                    )}
                  </Grid>
                  {/* ゲートウェイの名前 */}
                  <Grid item xs={5} lg={5}>
                    {tempGW['gateway-name']}
                    {/* 引継ぎ中は名称の後ろに追記する */}
                    {tempGW['exchange-gateway-id'] === ''
                      ? ''
                      : ` (${trans('GWActivate.exchange.message')})`}
                  </Grid>
                  {/* リトライボタン */}
                  {/* アクティベーション中は削除ボタンのみ */}
                  {/* 引継ぎ時はリトライボタンを非表示 */}
                  <Grid item xs={6} lg={6}>
                    <Grid container justifyContent="flex-end" spacing={1}>
                      <Grid item xs={'auto'}>
                        {tempGW['time-remaining'] === '0' &&
                        tempGW['exchange-gateway-id'] === '' ? (
                          retryDisabled[index] ? (
                            <Button variant="outlined" color="primary">
                              <CircularProgress size={25} />
                            </Button>
                          ) : (
                            <Button
                              onClick={() =>
                                retryGWAdd(
                                  tempGW['gateway-name'],
                                  tempGW['agency-code'],
                                  index
                                )
                              }
                              variant="outlined"
                              color="primary"
                              aria-label="RETRY"
                              startIcon={
                                <img
                                  className="gwSetting__contents__button__icon"
                                  src={iconBlack.RETRY.src}
                                  alt={`${trans(
                                    `GWActivate.image.alternativeText.${iconBlack.RETRY.alt}`
                                  )}`}
                                />
                              }
                              id={'mv-btnRetry' + tempGW['gateway-name']}>
                              {trans('GWActivate.buttonTxt.retry')}
                            </Button>
                          )
                        ) : (
                          <></>
                        )}
                      </Grid>
                      {/* 削除ボタン */}
                      <Grid item xs={'auto'}>
                        {deleteDisabled[index] ? (
                          <Button variant="contained" color="primary">
                            <CircularProgress size={25} color="inherit" />
                          </Button>
                        ) : (
                          <Button
                            onClick={() =>
                              deleteTempGW(tempGW['gateway-name'], index)
                            }
                            variant="contained"
                            color="primary"
                            aria-label="DELETE"
                            startIcon={
                              tempGW['exchange-gateway-id'] === '' ? (
                                <img
                                  className="gwSetting__contents__deleteButton__icon"
                                  src={iconWhite.DELETE.src}
                                  alt={`${trans(
                                    `GWActivate.image.alternativeText.${iconWhite.DELETE.alt}`
                                  )}`}
                                />
                              ) : (
                                <img
                                  className="gwSetting__contents__deleteButton__icon"
                                  src={iconWhite.MISSING.src}
                                  alt={`${trans(
                                    `GWActivate.image.alternativeText.${iconWhite.MISSING.alt}`
                                  )}`}
                                />
                              )
                            }
                            id={'mv-btnDelete' + tempGW['gateway-name']}>
                            {/* 新規追加と引継ぎでボタンの表記を切り替える */}
                            <Box className="gwSetting__contents__deleteButton__text">
                              {tempGW['exchange-gateway-id'] === ''
                                ? trans('GWActivate.buttonTxt.delete')
                                : trans('GWActivate.buttonTxt.cancel')}
                            </Box>
                          </Button>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                {/* ゲートウェイの情報 */}
                <Stack spacing={2} className="gwSetting__contents__contents">
                  {/* 現在のゲートウェイの状態 */}
                  <CardContents
                    class="gwSetting__contents__contents__underLine"
                    valueDescription={trans(
                      'GWActivate.item.title.gatewayStatus'
                    )}
                    value={
                      tempGW['time-remaining'] !== '0'
                        ? `${trans(
                            'GWActivate.item.value.gatewayStatus.waiting'
                          )}`
                        : `${trans(
                            'GWActivate.item.value.gatewayStatus.timeOut'
                          )}`
                    }
                  />
                  {/* アクティベーションキー */}
                  <CardContents
                    class="gwSetting__contents__contents__underLine"
                    valueDescription={trans(
                      'GWActivate.item.title.activationKey'
                    )}
                    value={
                      tempGW['time-remaining'] === '0'
                        ? '----'
                        : tempGW['certificate-path']
                    }
                    valueID={`mv-activateCode${tempGW['gateway-name']}`}
                  />
                  {/* アクティベーションキー有効期限 */}
                  <CardContents
                    class="gwSetting__contents__contents__underLine"
                    valueDescription={trans('GWActivate.item.title.keyExpires')}
                    value={
                      props.restActivationTime[index] !== undefined ? (
                        TimeRemainCalc(index)
                      ) : (
                        <></>
                      )
                    }
                  />
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))
      ) : (
        <></>
      )}
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        id={'mv-gwActivateErrDlgBtn'}
      />
    </>
  );
};
export default GWActivate;
